/**
 * Authenticated file downloads.
 *
 * Ported from `lib/downloadAuthenticatedFile.ts`, with `requestBlob` doing the fetch so the
 * error handling is the transport's rather than a second, weaker copy of it.
 *
 * **A plain `<a href download>` cannot do this.** The ID token has to travel as an
 * `Authorization` header, and a top-level navigation triggered by clicking a link cannot carry
 * one. Fetching the bytes and pointing a synthetic anchor at the resulting `blob:` URL is the
 * way round it — and it is the same fix that makes a cross-origin response downloadable, which
 * this app needs for every file because the API is always on another origin.
 */

import { requestBlob } from "./client";

/** `attachment; filename="x.csv"` → `x.csv`. Null when the header is absent or unparseable. */
function fileNameFromContentDisposition(header: string | null): string | null {
  const match = header?.match(/filename="([^"]+)"/);
  return match?.[1] ?? null;
}

async function downloadAuthenticatedFile(
  path: string,
  idToken: string | null,
  fallbackFileName: string,
): Promise<void> {
  const response = await requestBlob(path, idToken);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const fileName =
    fileNameFromContentDisposition(response.headers.get("content-disposition")) ?? fallbackFileName;

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Deferred rather than immediate: revoking synchronously can race the browser's own read of
  // the blob URL in some engines, which drops the download with no error anywhere.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** The AI image register as CSV, for a downstream legal or governance workflow. */
export async function downloadRegisterCsv(idToken: string | null): Promise<void> {
  await downloadAuthenticatedFile("/admin/register/export", idToken, "ai-image-register.csv");
}

/** One generation's integrity record — the brief, the controls and the guardrail outcome. */
export async function downloadIntegrityRecord(
  generationId: string,
  idToken: string | null,
): Promise<void> {
  await downloadAuthenticatedFile(
    `/generate/integrity-record?id=${encodeURIComponent(generationId)}`,
    idToken,
    "integrity-record.html",
  );
}

/** One generation's full-resolution image, saved to disk rather than opened in a viewer. */
export async function downloadGeneration(
  generationId: string,
  idToken: string | null,
): Promise<void> {
  await downloadAuthenticatedFile(
    `/generate/download?id=${encodeURIComponent(generationId)}`,
    idToken,
    "generated-image.png",
  );
}

/**
 * The integrity record, opened in a new tab rather than saved.
 *
 * **The tab is opened before the `await`, deliberately.** Opened after the fetch resolves, the
 * call is no longer inside the original click as far as the browser is concerned and the popup
 * blocker kills it silently. Opening first and writing into it afterwards keeps it a direct
 * result of the user's gesture.
 */
export async function viewIntegrityRecord(
  generationId: string,
  idToken: string | null,
): Promise<void> {
  const newTab = window.open("", "_blank");

  try {
    const response = await requestBlob(
      `/generate/integrity-record?id=${encodeURIComponent(generationId)}`,
      idToken,
    );
    if (!newTab) {
      // Blocked outright. There is nothing to write into, and forcing a navigation now would
      // not be a trusted gesture either — the caller shows its own error for this.
      throw new Error("The browser blocked opening a new tab for the record.");
    }
    const html = await response.text();
    newTab.document.open();
    newTab.document.write(html);
    newTab.document.close();
  } catch (error) {
    newTab?.close();
    throw error;
  }
}

/** One generation cropped to a named social or web placement, around its focal point. */
export async function downloadCropExport(
  generationId: string,
  preset: string,
  idToken: string | null,
): Promise<void> {
  await downloadAuthenticatedFile(
    `/generate/export?id=${encodeURIComponent(generationId)}&preset=${encodeURIComponent(preset)}`,
    idToken,
    `generation-${preset}.jpg`,
  );
}
